import { useEffect, useRef } from 'react';
import { hasFinePointer, prefersReducedMotion } from './prism/motion';

// elebyte.io's cursor (Experience/src/ui/Cursor.jsx): an instant cyan dot and a trailing ring. Over
// anything clickable the ring grows, tints and skews to the prism angle; pressing squashes it; over a
// text field it steps aside for the native caret. Fine pointers only, and off under reduced motion.
// Support has no gsap, so the ring eases on its own requestAnimationFrame, which runs only while it
// is still catching up and stops once it has settled.
export const INTERACTIVE = 'a[href], button:not(:disabled), select:not(:disabled), summary, label, [role="button"], [role="tab"], [role="option"], [data-cursor]';
export const TEXT_FIELD = 'textarea, [contenteditable]:not([contenteditable="false"]), '
  + 'input:not([type="checkbox"], [type="radio"], [type="range"], [type="button"], [type="submit"], '
  + '[type="reset"], [type="color"], [type="file"], [type="image"], [type="hidden"])';

const RING_EASE = 0.28; // share of the gap the ring closes each frame

export default function Cursor() {
  const enabled = useRef(hasFinePointer() && !prefersReducedMotion()).current;
  const rootRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!enabled || !root || !dot || !ring) return undefined;

    document.body.classList.add('has-cursor');
    const target = { x: -100, y: -100 };
    const at = { x: -100, y: -100 };
    let frame = 0;

    const step = () => {
      at.x += (target.x - at.x) * RING_EASE;
      at.y += (target.y - at.y) * RING_EASE;
      ring.style.transform = `translate3d(${at.x}px, ${at.y}px, 0)`;
      frame = Math.abs(target.x - at.x) + Math.abs(target.y - at.y) > 0.3 ? requestAnimationFrame(step) : 0;
    };
    const onMove = (e: PointerEvent) => {
      target.x = e.clientX;
      target.y = e.clientY;
      dot.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      root.classList.add('is-visible');
      if (!frame) frame = requestAnimationFrame(step);
    };
    const onOver = (e: Event) => {
      const el = e.target as Element | null;
      const text = Boolean(el?.closest?.(TEXT_FIELD));
      root.classList.toggle('is-text', text);
      root.classList.toggle('is-hover', !text && Boolean(el?.closest?.(INTERACTIVE)));
    };
    const onDown = () => root.classList.add('is-down');
    const onUp = () => root.classList.remove('is-down');
    const onLeave = () => root.classList.remove('is-visible');
    const html = document.documentElement;

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerover', onOver);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    html.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerover', onOver);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      html.removeEventListener('mouseleave', onLeave);
      document.body.classList.remove('has-cursor');
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <div className="cursor" ref={rootRef} aria-hidden="true">
      <div className="cursor__ring" ref={ringRef}><span className="cursor__ring-shape" /></div>
      <div className="cursor__dot" ref={dotRef} />
    </div>
  );
}
